<template>
    <div class="upgrade-page" :style="rootStyles">
        <!-- Loading inicial -->
        <div v-if="loading" class="state-screen">
            <v-progress-circular indeterminate size="56" :color="primaryColor || 'primary'" />
            <p class="state-screen__hint">Carregando...</p>
        </div>

        <!-- Erro de carregamento -->
        <div v-else-if="loadError" class="state-screen">
            <v-icon size="64" color="grey">mdi-alert-circle-outline</v-icon>
            <h2 class="state-screen__title">{{ loadError }}</h2>
            <p class="state-screen__hint">
                Verifique se o link recebido por e-mail está completo e tente novamente.
            </p>
        </div>

        <!-- Tela de sucesso -->
        <div v-else-if="confirmed" class="state-screen state-screen--success">
            <div class="success-card">
                <div class="success-icon">
                    <v-icon size="56" color="#fff">mdi-check</v-icon>
                </div>
                <h2 class="success-title">Obrigado, {{ firstName }}!</h2>
                <p class="success-text">
                    Sua doação recorrente foi atualizada para
                    <strong>{{ formatCurrency(selectedOption.newTotalValue) }}</strong>
                    <span class="success-text__muted">/ {{ recurrenceLabel }}</span>.
                </p>
                <p class="success-text success-text--muted">
                    Você receberá um e-mail de confirmação em instantes.
                </p>
            </div>
        </div>

        <!-- Formulário -->
        <div v-else class="upgrade-content">
            <header class="upgrade-hero" :style="heroStyles">
                <div class="upgrade-hero__overlay"></div>
                <div class="upgrade-hero__inner">
                    <p class="upgrade-hero__eyebrow">Você está fazendo a diferença com</p>
                    <h1 class="upgrade-hero__title">{{ data.campaignName }}</h1>
                </div>
            </header>

            <main class="upgrade-card">
                <!-- Logo da instituição no topo do card (sobre fundo branco) - daily 30/06 -->
                <div v-if="data.branding.logoUrl" class="card-logo">
                    <img
                        :src="data.branding.logoUrl"
                        :alt="data.campaignName"
                        class="card-logo__img"
                    />
                </div>

                <section class="card-greeting">
                    <h2 class="card-greeting__title">Olá, {{ firstName }}!</h2>
                    <!-- context vem do parâmetro u (editável pelo usuário): SEMPRE interpolação, nunca v-html -->
                    <p v-if="data.context" class="card-greeting__text">{{ data.context }}</p>
                    <p v-else class="card-greeting__text">
                        A sua contribuição mantém vivos projetos que transformam histórias.
                        Que tal aumentar o seu impacto?
                    </p>
                </section>

                <section class="card-current">
                    <span class="card-current__label">Sua doação atual</span>
                    <div class="card-current__value">
                        <strong>{{ formatCurrency(data.currentValue) }}</strong>
                        <span class="card-current__period">/ {{ recurrenceLabel }}</span>
                    </div>
                </section>

                <section class="card-options">
                    <h3 class="card-options__title">Escolha o novo valor</h3>

                    <!-- Layout dinâmico: 1, 2, 3+ opções (contrato de upgrade via URL) -->
                    <div class="options-grid" :style="optionsGridStyle">
                        <button
                            v-for="opt in data.options"
                            :key="opt.newTotalValue"
                            type="button"
                            class="option"
                            :class="{
                                'option--selected': isSelected(opt),
                                'option--recommended': opt.isRecommended,
                            }"
                            :aria-pressed="isSelected(opt) ? 'true' : 'false'"
                            @click="selectOption(opt)"
                        >
                            <span v-if="opt.isRecommended" class="option__badge">Recomendado</span>
                            <span class="option__total">
                                {{ formatCurrency(opt.newTotalValue) }}
                            </span>
                            <span class="option__period">/ {{ recurrenceLabel }}</span>
                            <span class="option__meta">
                                <span class="option__increment">+ {{ formatCurrency(opt.incrementValue) }}</span>
                                <span v-if="opt.incrementPercentage" class="option__growth">
                                    <span aria-hidden="true">↑</span>
                                    {{ formatPercent(opt.incrementPercentage) }}
                                </span>
                            </span>
                        </button>
                    </div>

                    <!-- "Outro valor" — campo aberto (daily 30/06) -->
                    <div class="custom-amount" :class="{ 'custom-amount--active': customMode }">
                        <button
                            type="button"
                            class="custom-amount__toggle"
                            @click="toggleCustomAmount"
                        >
                            <span class="custom-amount__toggle-icon">
                                <v-icon small :color="customMode ? 'primary' : 'grey'">
                                    {{ customMode ? 'mdi-check-circle' : 'mdi-plus-circle-outline' }}
                                </v-icon>
                            </span>
                            <span>Quero acrescentar outro valor</span>
                        </button>

                        <div v-if="customMode" class="custom-amount__form">
                            <label for="customIncrementInput" class="custom-amount__label">
                                Quanto a mais {{ recurrenceAdverb }}?
                            </label>
                            <div class="custom-amount__input-row">
                                <span class="custom-amount__currency">R$</span>
                                <input
                                    id="customIncrementInput"
                                    v-model.number="customIncrement"
                                    type="number"
                                    min="5"
                                    step="5"
                                    inputmode="numeric"
                                    placeholder="0"
                                    class="custom-amount__input"
                                />
                            </div>
                            <p v-if="customValid" class="custom-amount__preview">
                                Novo valor total:
                                <strong>{{ formatCurrency(customNewTotal) }}</strong>
                                / {{ recurrenceLabel }}
                            </p>
                            <p v-else-if="customIncrement" class="custom-amount__hint">
                                O acréscimo precisa ser múltiplo de R$ 5,00 (mínimo R$ 5,00).
                            </p>
                            <p v-else class="custom-amount__hint">
                                Digite o valor que deseja somar à sua doação atual.
                            </p>
                        </div>
                    </div>

                    <p v-if="submitError" class="card-error" role="alert">
                        <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
                        {{ submitError }}
                    </p>
                </section>

                <section class="card-consent">
                    <label class="consent-row">
                        <input
                            type="checkbox"
                            v-model="acceptedTerm"
                            class="consent-row__check"
                        />
                        <span class="consent-row__text">
                            Declaro que li e concordo com o
                            <a
                                href="#"
                                class="consent-row__link"
                                @click.prevent="showTermModal = true"
                            >termo de anuência</a>
                            para atualização da minha doação recorrente.
                        </span>
                    </label>
                </section>

                <section class="card-action">
                    <v-btn
                        class="confirm-btn"
                        large
                        depressed
                        block
                        :disabled="!canConfirm"
                        :loading="submitting"
                        @click="onConfirm"
                    >
                        Confirmar nova doação
                    </v-btn>
                </section>

                <footer class="card-footer">
                    <div class="card-footer__row">
                        <span class="card-footer__label">E-mail</span>
                        <span class="card-footer__value">{{ data.donorEmail }}</span>
                    </div>
                    <div class="card-footer__row">
                        <span class="card-footer__label">CPF</span>
                        <span class="card-footer__value">{{ data.donorDocument }}</span>
                    </div>
                </footer>
            </main>
        </div>

        <!-- Modal do termo de anuência (daily 30/06: aberto apenas se o doador clicar no link) -->
        <v-dialog v-model="showTermModal" max-width="560" persistent>
            <v-card class="term-modal" :style="rootStyles">
                <div class="term-modal__header">
                    <h3 class="term-modal__title">Termo de anuência</h3>
                    <button
                        type="button"
                        class="term-modal__close"
                        aria-label="Fechar"
                        @click="showTermModal = false"
                    >
                        <v-icon>mdi-close</v-icon>
                    </button>
                </div>

                <div class="term-modal__body">
                    <p>
                        Eu, <strong>{{ data.donorName }}</strong>{{ data.donorDocument ? `, CPF ${data.donorDocument},` : ',' }}
                        <strong>autorizo</strong>, de forma voluntária, a atualização da minha
                        doação recorrente para a campanha
                        <strong>{{ data.campaignName }}</strong>:
                    </p>

                    <ul class="term-modal__details">
                        <li>
                            <span>Valor atual</span>
                            <strong>{{ formatCurrency(data.currentValue) }} / {{ recurrenceLabel }}</strong>
                        </li>
                        <li v-if="previewNewTotal !== null">
                            <span>Novo valor</span>
                            <strong>{{ formatCurrency(previewNewTotal) }} / {{ recurrenceLabel }}</strong>
                        </li>
                    </ul>

                    <p class="term-modal__legal">
                        Declaro que minha doação é voluntária. Estou ciente de que, no caso de
                        doações recorrentes, posso solicitar o cancelamento ou voltar ao valor
                        anterior a qualquer momento entrando em contato com a instituição
                        responsável pela campanha.
                    </p>
                </div>

                <div class="term-modal__footer">
                    <v-btn
                        class="term-modal__accept"
                        large
                        depressed
                        @click="onAcceptTerm"
                    >
                        Concordo
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import UpgradeDonationService from "@/scripts/services/upgradeDonation.service";
import { UpgradeDonation, parseAucColor } from "@/scripts/models/UpgradeDonation.model";
import { loadCampaignFonts } from "@/scripts/helpers/fontLoader.helper";

export default {
    name: "UpgradeDonation",

    data() {
        return {
            loading: true,
            loadError: null,

            data: new UpgradeDonation(),
            selectedOption: null,

            // "Outro valor" — 4ª opção aberta (daily 30/06)
            customMode: false,
            customIncrement: null,

            // Termo de anuência (daily 30/06 — baixa fricção: checkbox inline + modal opcional)
            acceptedTerm: false,
            showTermModal: false,

            submitting: false,
            submitError: null,
            confirmed: false,

            // CSS injection é montado num <style> dedicado para permitir reaplicar/limpar.
            cssInjectionEl: null,

            service: new UpgradeDonationService(),
        };
    },

    computed: {
        checkoutCode() {
            return this.$route.query.code || this.$route.query.checkoutCode || null;
        },

        /**
         * Parâmetro `u` do link (contrato CONTRATO_UPGRADE_URL.md). Repassado cru ao back,
         * que decodifica e recalcula as opções; ausente/inválido → opções padrão.
         */
        upgradeConfig() {
            const u = this.$route.query.u;
            return typeof u === "string" && u ? u : null;
        },

        /**
         * Até 3 colunas; 1 ou 2 opções ocupam a largura toda. 4+ quebram em linhas.
         */
        optionsGridStyle() {
            const count = (this.data.options || []).length;
            return { "--options-cols": Math.max(1, Math.min(count, 3)) };
        },

        firstName() {
            return (this.data.donorName || "").split(" ")[0] || "Doador";
        },

        primaryColor() {
            return parseAucColor(this.data.branding.themePrimaria);
        },

        /**
         * Normaliza a frequência para algo curto e amigável na UI.
         * "Recorrência Mensal" → "Mensal", "Trimestral" → "Trimestral".
         */
        recurrenceLabel() {
            const raw = (this.data.recurrencePeriod || "").trim();
            if (!raw) return "mês";
            return raw.replace(/^Recorr[êe]ncia\s+/i, "");
        },

        /**
         * CSS variables aplicadas no escopo da página — não vazam para o resto do app.
         */
        rootStyles() {
            const b = this.data.branding || {};
            const vars = {
                "--theme-primaria":         parseAucColor(b.themePrimaria),
                "--theme-secundaria":       parseAucColor(b.themeSecundaria),
                "--theme-destaque":         parseAucColor(b.themeDestaque),
                "--theme-fundo":            parseAucColor(b.themeFundo),
                "--theme-padrao":           parseAucColor(b.themePadrao),
                "--theme-gradiente-start":  parseAucColor(b.themeGradienteStart),
                "--theme-gradiente-end":    parseAucColor(b.themeGradienteEnd),
                "--theme-texto":            parseAucColor(b.themeTexto),
                "--font-principal":         b.fontPrincipal ? `${b.fontPrincipal}, sans-serif` : null,
                "--font-destaques":         b.fontDestaques ? `${b.fontDestaques}, serif` : null,
            };
            return Object.entries(vars).reduce((acc, [k, v]) => {
                if (v) acc[k] = v;
                return acc;
            }, {});
        },

        heroStyles() {
            const hero = this.data.branding && this.data.branding.heroImageUrl;
            if (!hero) return {};
            return { "background-image": `url('${hero}')` };
        },

        /**
         * Valor digitado no campo "Outro valor" só é válido se:
         *  - >= R$ 5,00 (incremento mínimo, mesma regra do back)
         *  - múltiplo de R$ 5,00 (consistência com as opções fixas)
         */
        customValid() {
            const v = Number(this.customIncrement);
            return !!v && v >= 5 && v % 5 === 0;
        },

        /**
         * Novo total quando "Outro valor" está ativo e válido. null caso contrário.
         */
        customNewTotal() {
            if (!this.customValid) return null;
            return Number(this.data.currentValue || 0) + Number(this.customIncrement);
        },

        /**
         * Pode confirmar quando há uma opção fixa selecionada OU o "Outro valor" é válido,
         * E o doador aceitou o termo de anuência.
         */
        canConfirm() {
            if (this.submitting) return false;
            if (!this.acceptedTerm) return false;
            if (this.selectedOption) return true;
            if (this.customMode && this.customValid) return true;
            return false;
        },

        /**
         * Novo total a ser exibido no termo de anuência (selectedOption fixa ou custom).
         */
        previewNewTotal() {
            if (this.selectedOption) return this.selectedOption.newTotalValue;
            if (this.customMode && this.customValid) return this.customNewTotal;
            return null;
        },

        /**
         * Adverbio temporal para o label do campo "Outro valor" (mensal → mensalmente).
         */
        recurrenceAdverb() {
            const map = {
                "mensal": "mensalmente",
                "bimestral": "bimestralmente",
                "trimestral": "trimestralmente",
                "quadrimestral": "quadrimestralmente",
                "semestral": "semestralmente",
                "anual": "anualmente",
            };
            const key = (this.recurrenceLabel || "").toLowerCase();
            return map[key] || key;
        },
    },

    async created() {
        if (!this.checkoutCode) {
            this.loadError = "Link inválido.";
            this.loading = false;
            return;
        }
        await this.load();
    },

    beforeDestroy() {
        if (this.cssInjectionEl) {
            this.cssInjectionEl.remove();
            this.cssInjectionEl = null;
        }
    },

    methods: {
        isSelected(opt) {
            return !!this.selectedOption && this.selectedOption.newTotalValue === opt.newTotalValue;
        },

        /**
         * Selecionar uma opção fixa desliga o modo "Outro valor".
         */
        selectOption(opt) {
            this.selectedOption = opt;
            this.customMode = false;
            this.customIncrement = null;
        },

        /**
         * Abrir / fechar o modo "Outro valor" — desseleciona qualquer opção fixa.
         */
        toggleCustomAmount() {
            if (this.customMode) {
                this.customMode = false;
                this.customIncrement = null;
            } else {
                this.customMode = true;
                this.selectedOption = null;
            }
        },

        /**
         * Clique em "Concordo" no modal — marca o checkbox e fecha.
         */
        onAcceptTerm() {
            this.acceptedTerm = true;
            this.showTermModal = false;
        },

        async load() {
            this.loading = true;
            this.loadError = null;
            try {
                const payload = await this.service.getByCheckoutCode(this.checkoutCode, this.upgradeConfig);
                this.data = new UpgradeDonation(payload);
                // Pré-seleciona a opção recomendada pela campanha de captação (quando houver).
                this.selectedOption = this.data.options.find((o) => o.isRecommended) || null;
                // Injeta as fontes da campanha (Google Fonts) — Nunito já vem do index.html.
                // Sem isso, FontDestaques (ex.: IBM Plex Serif) cai pra serifa padrão (Times).
                loadCampaignFonts(this.data.branding.fontPrincipal, this.data.branding.fontDestaques);
                this.applyCssInjection(this.data.branding.cssInjection);
            } catch (err) {
                this.loadError = this.extractErrorMessage(err) || "Não foi possível carregar os dados.";
            } finally {
                this.loading = false;
            }
        },

        async onConfirm() {
            if (!this.canConfirm) return;

            // Se o doador escolheu "Outro valor", monta um selectedOption sintético
            // para alimentar a tela de sucesso com newTotalValue/incrementValue corretos.
            if (this.customMode && !this.selectedOption) {
                this.selectedOption = {
                    incrementValue: Number(this.customIncrement),
                    newTotalValue: this.customNewTotal,
                    incrementPercentage: 0,
                };
            }

            this.submitting = true;
            this.submitError = null;
            try {
                await this.service.confirm(
                    this.checkoutCode,
                    this.selectedOption.newTotalValue,
                    this.upgradeConfig
                );
                this.confirmed = true;
            } catch (err) {
                this.submitError = this.extractErrorMessage(err)
                    || "Não foi possível confirmar agora. Tente novamente em instantes.";
            } finally {
                this.submitting = false;
            }
        },

        applyCssInjection(css) {
            if (!css) return;
            const el = document.createElement("style");
            el.setAttribute("data-source", "upgrade-donation");
            el.textContent = css;
            document.head.appendChild(el);
            this.cssInjectionEl = el;
        },

        extractErrorMessage(err) {
            if (!err) return null;
            if (err.errorMessage) return err.errorMessage;
            if (err.response && err.response.data) {
                return err.response.data.errorMessage || err.response.data.message || null;
            }
            if (err.message) return err.message;
            return null;
        },

        formatPercent(value) {
            const v = Number(value) || 0;
            return `${v.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;
        },

        formatCurrency(value) {
            const v = Number(value) || 0;
            return v.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
            });
        },
    },
};
</script>

<style scoped>
/* ---------- Reset / canvas ---------- */
.upgrade-page {
    min-height: 100vh;
    width: 100%;
    overflow-x: hidden;
    background: var(--theme-fundo, #f4f6fb);
    color: var(--theme-texto, #1f2a44);
    font-family: var(--font-principal, "Roboto", -apple-system, BlinkMacSystemFont, sans-serif);
    padding-bottom: 56px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
}
.upgrade-page * {
    box-sizing: border-box;
}

/* ---------- Estados (loading/erro/sucesso) ---------- */
.state-screen {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 32px 20px;
}
.state-screen__title {
    margin-top: 16px;
    font-family: var(--font-destaques, inherit);
    font-size: 22px;
    font-weight: 600;
}
.state-screen__hint {
    margin-top: 8px;
    color: #6b7392;
    max-width: 380px;
}
.state-screen--success {
    background:
        radial-gradient(
            circle at 50% -20%,
            color-mix(in srgb, var(--theme-primaria, #3fa2f7) 18%, transparent),
            transparent 70%
        );
}
.success-card {
    background: #fff;
    border-radius: 20px;
    padding: 40px 28px 32px;
    box-shadow: 0 18px 40px -20px rgba(31, 42, 68, 0.18);
    max-width: 460px;
    width: 100%;
    text-align: center;
}
.success-icon {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: linear-gradient(
        135deg,
        var(--theme-primaria, #3fa2f7),
        var(--theme-secundaria, #297fca)
    );
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 10px 24px -6px color-mix(in srgb, var(--theme-primaria, #3fa2f7) 60%, transparent);
}
.success-title {
    margin-top: 20px;
    font-family: var(--font-destaques, inherit);
    font-size: 26px;
    font-weight: 700;
    color: var(--theme-texto, #1f2a44);
}
.success-text {
    margin-top: 12px;
    font-size: 16px;
    line-height: 1.5;
}
.success-text strong {
    color: var(--theme-primaria, #3fa2f7);
    font-weight: 700;
}
.success-text__muted,
.success-text--muted {
    color: #6b7392;
    font-weight: 400;
    font-size: 14px;
}

/* ---------- Hero ---------- */
.upgrade-hero {
    position: relative;
    padding: 44px 20px 96px;
    color: #fff;
    background: linear-gradient(
        135deg,
        var(--theme-gradiente-start, #297fca) 0%,
        var(--theme-gradiente-end, #3fa2f7) 100%
    );
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    text-align: center;
}
.upgrade-hero__overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
        180deg,
        rgba(15, 26, 56, 0.25) 0%,
        rgba(15, 26, 56, 0.55) 100%
    );
    pointer-events: none;
}
.upgrade-hero__inner {
    position: relative;
    z-index: 1;
    max-width: 640px;
    margin: 0 auto;
}
.upgrade-hero__eyebrow {
    margin: 0 0 6px;
    font-size: 13px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: 0.85;
}
.upgrade-hero__title {
    margin: 0;
    font-family: var(--font-destaques, inherit);
    font-size: clamp(22px, 4vw, 30px);
    font-weight: 700;
    line-height: 1.25;
    word-break: break-word;
}

/* ---------- Card central ---------- */
.upgrade-card {
    position: relative;
    max-width: 640px;
    width: calc(100% - 32px);
    margin: -64px auto 0;
    background: #fff;
    border-radius: 24px;
    box-shadow: 0 24px 50px -28px rgba(15, 26, 56, 0.25);
    padding: 32px clamp(20px, 4vw, 36px) 28px;
    overflow: hidden;
}

/* Logo da instituição no topo do card branco (daily 30/06) */
.card-logo {
    display: flex;
    justify-content: center;
    margin: -8px 0 20px;
}
.card-logo__img {
    max-width: 180px;
    max-height: 72px;
    object-fit: contain;
}

.card-greeting__title {
    margin: 0;
    font-family: var(--font-destaques, inherit);
    font-size: clamp(22px, 3.4vw, 28px);
    font-weight: 700;
    color: var(--theme-texto, #1f2a44);
}
.card-greeting__text {
    margin: 8px 0 0;
    color: #4d5878;
    font-size: 15px;
    line-height: 1.55;
}

/* "Doação atual" - destaque do estado atual */
.card-current {
    margin-top: 24px;
    padding: 18px 20px;
    border-radius: 14px;
    background: color-mix(in srgb, var(--theme-primaria, #3fa2f7) 8%, #fff);
    border: 1px solid color-mix(in srgb, var(--theme-primaria, #3fa2f7) 22%, transparent);
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.card-current__label {
    font-size: 13px;
    letter-spacing: 0.04em;
    color: #4d5878;
    text-transform: uppercase;
}
.card-current__value {
    display: flex;
    align-items: baseline;
    gap: 6px;
    flex-wrap: wrap;
}
.card-current__value strong {
    font-family: var(--font-destaques, inherit);
    font-size: clamp(24px, 4vw, 30px);
    font-weight: 700;
    color: var(--theme-primaria, #297fca);
}
.card-current__period {
    color: #6b7392;
    font-size: 14px;
}

/* Opções */
.card-options {
    margin-top: 28px;
}
.card-options__title {
    margin: 0 0 14px;
    font-family: var(--font-destaques, inherit);
    font-size: 17px;
    font-weight: 600;
    color: var(--theme-texto, #1f2a44);
}
.options-grid {
    display: grid;
    grid-template-columns: repeat(var(--options-cols, 3), minmax(0, 1fr));
    gap: 16px 12px;
    /* espaço para o selo "Recomendado", que sobe 10px acima do box */
    padding-top: 10px;
}
@media (max-width: 560px) {
    .options-grid {
        grid-template-columns: 1fr;
    }
}

.option {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 22px 14px 18px;
    border: 1.5px solid #e3e7f1;
    border-radius: 18px;
    background: #fff;
    cursor: pointer;
    transition: transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
    color: var(--theme-texto, #1f2a44);
    font-family: inherit;
    text-align: center;
    min-width: 0;
}
.option:hover {
    border-color: color-mix(in srgb, var(--theme-primaria, #3fa2f7) 55%, #e3e7f1);
    transform: translateY(-1px);
}
.option:focus-visible {
    outline: 2px solid var(--theme-primaria, #3fa2f7);
    outline-offset: 2px;
}
.option--selected {
    border-color: var(--theme-primaria, #3fa2f7);
    background: color-mix(in srgb, var(--theme-primaria, #3fa2f7) 7%, #fff);
    box-shadow: 0 10px 24px -16px color-mix(in srgb, var(--theme-primaria, #3fa2f7) 80%, transparent);
}
.option--selected::after {
    content: "";
    position: absolute;
    top: 10px;
    right: 10px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--theme-primaria, #3fa2f7);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 14px;
}

.option__total {
    font-family: var(--font-destaques, inherit);
    font-size: clamp(22px, 3.6vw, 26px);
    font-weight: 700;
    color: var(--theme-texto, #1f2a44);
    word-break: break-word;
    line-height: 1.1;
}
.option__period {
    font-size: 12px;
    color: #6b7392;
}
.option__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 12px;
    color: #6b7392;
    margin-top: 6px;
}
.option__increment {
    font-weight: 600;
    color: var(--theme-texto, #1f2a44);
}
.option__growth {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 2px 8px;
    border-radius: 999px;
    font-weight: 700;
    color: #1b7f4b;
    background: #e6f6ed;
}
.option__badge {
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
    white-space: nowrap;
    color: #fff;
    background: var(--theme-primaria, #3fa2f7);
}
.option--recommended {
    border-color: color-mix(in srgb, var(--theme-primaria, #3fa2f7) 45%, #e3e7f1);
}

/* "Outro valor" — 4ª opção aberta (daily 30/06) */
.custom-amount {
    margin-top: 14px;
    border: 1.5px dashed #d6dbe8;
    border-radius: 18px;
    background: #fafbfd;
    transition: border-color 0.15s ease, background 0.15s ease;
}
.custom-amount--active {
    border-style: solid;
    border-color: var(--theme-primaria, #3fa2f7);
    background: color-mix(in srgb, var(--theme-primaria, #3fa2f7) 5%, #fff);
}
.custom-amount__toggle {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 18px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: var(--theme-texto, #1f2a44);
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
}
.custom-amount__toggle:focus-visible {
    outline: 2px solid var(--theme-primaria, #3fa2f7);
    outline-offset: 2px;
    border-radius: 18px;
}
.custom-amount__form {
    padding: 0 18px 18px;
}
.custom-amount__label {
    display: block;
    font-size: 13px;
    color: #4d5878;
    margin-bottom: 8px;
}
.custom-amount__input-row {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff;
    border: 1.5px solid #e3e7f1;
    border-radius: 12px;
    padding: 10px 14px;
    transition: border-color 0.15s ease;
}
.custom-amount__input-row:focus-within {
    border-color: var(--theme-primaria, #3fa2f7);
}
.custom-amount__currency {
    font-weight: 700;
    color: var(--theme-texto, #1f2a44);
    font-size: 16px;
}
.custom-amount__input {
    flex: 1;
    border: none;
    outline: none;
    font-family: var(--font-destaques, inherit);
    font-size: clamp(18px, 3vw, 22px);
    font-weight: 700;
    color: var(--theme-texto, #1f2a44);
    background: transparent;
    min-width: 0;
    padding: 0;
}
/* Esconde o spinner do input number em todos os browsers */
.custom-amount__input::-webkit-outer-spin-button,
.custom-amount__input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
.custom-amount__input[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
}
.custom-amount__preview {
    margin: 12px 0 0;
    font-size: 14px;
    color: #4d5878;
}
.custom-amount__preview strong {
    color: var(--theme-primaria, #297fca);
    font-weight: 700;
}
.custom-amount__hint {
    margin: 10px 0 0;
    font-size: 12px;
    color: #6b7392;
}

/* Termo de anuência inline (daily 30/06) */
.card-consent {
    margin-top: 22px;
    padding-top: 18px;
    border-top: 1px solid #eef0f7;
}
.consent-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
    user-select: none;
}
.consent-row__check {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 2px;
    accent-color: var(--theme-primaria, #3fa2f7);
    cursor: pointer;
}
.consent-row__text {
    font-size: 13px;
    line-height: 1.5;
    color: #4d5878;
    flex: 1;
    min-width: 0;
}
.consent-row__link {
    color: var(--theme-primaria, #297fca);
    text-decoration: underline;
    font-weight: 600;
}
.consent-row__link:hover {
    text-decoration: none;
}

/* Modal do termo de anuência */
.term-modal {
    border-radius: 20px !important;
    overflow: hidden;
    font-family: var(--font-principal, "Roboto", sans-serif);
}
.term-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 24px;
    border-bottom: 1px solid #eef0f7;
}
.term-modal__title {
    margin: 0;
    font-family: var(--font-destaques, inherit);
    font-size: 20px;
    font-weight: 700;
    color: var(--theme-texto, #1f2a44);
}
.term-modal__close {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 6px;
    color: #6b7392;
    line-height: 0;
}
.term-modal__body {
    padding: 22px 24px 6px;
    color: var(--theme-texto, #1f2a44);
    font-size: 14px;
    line-height: 1.55;
}
.term-modal__body p {
    margin: 0 0 14px;
}
.term-modal__details {
    list-style: none;
    padding: 0;
    margin: 0 0 16px;
    border-radius: 12px;
    background: color-mix(in srgb, var(--theme-primaria, #3fa2f7) 6%, #fff);
    border: 1px solid color-mix(in srgb, var(--theme-primaria, #3fa2f7) 18%, transparent);
}
.term-modal__details li {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid color-mix(in srgb, var(--theme-primaria, #3fa2f7) 12%, transparent);
}
.term-modal__details li:last-child {
    border-bottom: none;
}
.term-modal__details span {
    color: #4d5878;
    font-size: 13px;
}
.term-modal__details strong {
    font-family: var(--font-destaques, inherit);
    color: var(--theme-primaria, #297fca);
    font-size: 15px;
}
.term-modal__legal {
    font-size: 12px;
    color: #6b7392;
    line-height: 1.55;
}
.term-modal__footer {
    padding: 12px 24px 22px;
    display: flex;
    justify-content: flex-end;
}
.term-modal .v-btn.term-modal__accept {
    background-color: var(--theme-primaria, #3fa2f7) !important;
    color: #fff !important;
    border-radius: 12px !important;
    height: 44px !important;
    padding: 0 28px !important;
    font-weight: 700;
    text-transform: none !important;
    box-shadow: 0 10px 20px -12px color-mix(in srgb, var(--theme-primaria, #3fa2f7) 80%, transparent) !important;
}

.card-error {
    margin: 16px 0 0;
    padding: 10px 12px;
    background: #fff5f5;
    border: 1px solid #f4cdcd;
    border-radius: 10px;
    color: #b3261e;
    font-size: 14px;
    display: flex;
    align-items: center;
}

/* CTA */
.card-action {
    margin-top: 28px;
}
.card-action .v-btn.confirm-btn {
    background-color: var(--theme-primaria, #3fa2f7) !important;
    color: #fff !important;
    border-radius: 12px !important;
    height: 52px !important;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: none !important;
    box-shadow: 0 12px 24px -14px color-mix(in srgb, var(--theme-primaria, #3fa2f7) 80%, transparent) !important;
}
.card-action .v-btn.confirm-btn.v-btn--disabled {
    background-color: #cfd5e6 !important;
    color: #fff !important;
    box-shadow: none !important;
}
.card-action__hint {
    margin: 12px 0 0;
    font-size: 12px;
    color: #6b7392;
    text-align: center;
    line-height: 1.5;
}

/* Footer com identificação do doador */
.card-footer {
    margin-top: 28px;
    padding-top: 18px;
    border-top: 1px solid #eef0f7;
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
}
.card-footer__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    min-width: 0;
}
.card-footer__label {
    font-size: 12px;
    color: #6b7392;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    flex-shrink: 0;
}
.card-footer__value {
    font-size: 14px;
    color: var(--theme-texto, #1f2a44);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    text-align: right;
}
</style>
